import { useQueryClient } from '@tanstack/react-query';
import { Button } from 'components/atoms';
import { Form } from 'components/organisms';
import { trackInitialValues, TrackTabs } from 'components/organisms/forms/track/TrackForm';
import { listQueryKeys } from 'constants/queryKey';
import { FormikHelpers } from 'formik';
import { useMutateOptions } from 'hooks';
import middleware from 'middleware';
import { updateMusicProdoctDigitalAlbumsFromTrackResponse } from 'middleware/music-product-digital/updateMusicProdoctDigitalAlbumsFromTrackResponse';
import { useStoreState } from 'providers/StoreStateContext';
import { useTranslation } from 'react-i18next';
import { PiX } from 'react-icons/pi';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePostTrack } from 'services/tracks';
import { usePostTrackChunkedFile, usePostTrackFile } from 'services/tracks/file';
import { ITrackMutationForm, ITrackResponse } from 'services/tracks/types';
import { STATUS } from 'services/types';
import { getLastPathname } from 'utils/navbar/getParentPathname';
import { onErrorShowToast } from 'utils/service/errorHandler';

const CreateTrack = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const createTrack = usePostTrack();
  const addTrackFile = usePostTrackFile();
  const addTrackChunkedFile = usePostTrackChunkedFile();
  const mutateOptions = useMutateOptions<ITrackMutationForm>();
  const { pathname } = useLocation();
  const { clearForms, trackForm, musicProductDigitalForm, setForm, clearForm } = useStoreState();
  const queryClient = useQueryClient();

  const onGoBack = async (track?: ITrackResponse) => {
    await queryClient.invalidateQueries({
      queryKey: [listQueryKeys.tracks],
    });
    await queryClient.invalidateQueries({
      queryKey: [listQueryKeys.tracksLightList],
    });
    if (musicProductDigitalForm) {
      if (track) {
        setForm(
          'musicProductDigitalForm',
          updateMusicProdoctDigitalAlbumsFromTrackResponse(track, musicProductDigitalForm),
        );
      }
      navigate(
        `/music-product-digital/${musicProductDigitalForm.id.length ? `edit/${musicProductDigitalForm.id}` : 'create'}/tracks`,
        { replace: true },
      );
      clearForm('trackForm');

      return;
    }
    if (track) {
      navigate(`/track/${track.id}/${getLastPathname(pathname)}`, { replace: true });
    }
    if (!track) {
      navigate('/track');
    }
    clearForms();
  };

  // const onGoToDetail = async (id: string) => {
  //   navigate(`/track/${id}/${getLastPathname(pathname)}`);
  //   await queryClient.invalidateQueries({
  //     queryKey: [listQueryKeys.tracks],
  //   });
  //   clearForms();
  // };

  const onCreateTrackFile = async (file: File, track: ITrackResponse) => {
    await addTrackChunkedFile.mutateAsync(
      { file, trackId: track.id, version: track.version },
      {
        onError: async (error) => {
          onErrorShowToast(error, 'toast-message.add-file');
          await onGoBack(track);
        },
        onSuccess: async () => {
          await onGoBack(track);
        },
      },
    );
  };

  const onCreateTrack = async (
    values: ITrackMutationForm,
    helpers: FormikHelpers<ITrackMutationForm>,
  ) => {
    const track = {
      ...middleware.track.formToQuery(values),
      waitingForFile: !!values.file,
    };

    await createTrack.mutateAsync(track, {
      ...mutateOptions(helpers),
      onSuccess: async (track) =>
        !!values.file ? await onCreateTrackFile(values.file, track) : await onGoBack(track),
    });
  };

  return (
    <Form.Track
      i18nextPrefix="track"
      initialValues={trackForm ? { ...trackForm, status: STATUS.draft } : trackInitialValues}
      onSubmit={onCreateTrack}
      tittle={t('track.title.create')}
      disableTabs={[TrackTabs.contracts]}
      customBackItem={
        <Button isClear type="button" onlyIcon onClick={() => onGoBack()}>
          <Button.Icon icon={<PiX />} />
        </Button>
      }
    />
  );
};

export default CreateTrack;
